import React, { useEffect, useState, useRef } from "react";
import {
    TableHead,
    TableBody,
    Table,
    TableContainer,
    TableCell,
    TableRow,
    Box,
    Pagination,
    Button,
    FormControl,
    Autocomplete,
    TextField,
    Typography,
    Grid,
    Card,
    CardContent,
    Paper,
    TablePagination,
    IconButton,
    InputLabel,
    Select,
    MenuItem,
} from "@mui/material";
import { commonRunQuery } from "../../util/API/loans";
import { PieChart, Pie, Cell, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend } from "recharts";
import "./LoanUserReportTab.scss";
import { styled } from "@mui/material/styles";
import { tableCellClasses } from "@mui/material/TableCell";
import pdfImage from "../../images/pdf-image.svg"
import { CSVLink } from "react-csv";
import moment from "moment";
import CSVImage from "../../images/csv-file.png";
import jsPDF from "jspdf";
import "jspdf-autotable";
import autoTable from "jspdf-autotable";

const LoanUserReportTab = ({ selectedUser }) => {

    const [reportsData, setReportsData] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [maxHeight, setMaxHeight] = useState(0);
    const [statusFilter, setStatusFilter] = useState(''); // 'Issued' or 'Pending'
    const [termFilter, setTermFilter] = useState('');

    const card1Ref = useRef(null);
    const card2Ref = useRef(null);

    const now = moment();
    const termOptions = ["No Term", "Daily", "Weekly", "Bi-Weekly", "Monthly", "Yearly"];

    const filteredData = reportsData.filter((row) => {
        const statusMatch = statusFilter 
            ? (statusFilter === "Issued" && row?.lr?.issued === "1") ||
              (statusFilter === "Pending" && row?.lr?.issued === "0") ||
              (statusFilter === "Cancelled" && row?.lcr?.cancellationStatus === "1") 
            : true;
        
        const termMatch = termFilter ? termFilter === termOptions[row?.lr?.termtype] : true;
    
        return statusMatch && termMatch;
    });

    useEffect(() => {
        if (card1Ref.current && card2Ref.current) {
          const card1Height = card1Ref.current.clientHeight;
          const card2Height = card2Ref.current.clientHeight;
    
          // Find the maximum height
          const maxCardHeight = Math.max(card1Height, card2Height);
            console.log("maxCardHeight-----------",maxCardHeight);
          // Set the height of both cards to be the same (the max height)
          setMaxHeight(maxCardHeight);
        }
    }, [card1Ref.current?.clientHeight, card2Ref.current?.clientHeight]);

    const StyledTableCell = styled(TableCell)(({ theme }) => ({
        [`&.${tableCellClasses.head}`]: {
        backgroundColor: "rgba(0,0,0, .1)",
        color: theme.palette.common.black,
        fontWeight: "bold",
        textAlign:"center",
        },
        [`&.${tableCellClasses.body}`]: {
        fontSize: 25,
        fontWeight: "600",
        },
    }));

    const titleRow = [
        `Loan Status`,
    ];

    const csvData = [
        // Add the title row as the first row
        titleRow,
        // Add an empty row to separate the title from the header
        [],

        //These will be header columns
        [
            'Request ID',
            'Product ID',
            'Request Amount',
            'Request Date',
            'Term Type',
            'Status',
            'Comments',
            'Is Cancelled'            
        ],

        // Add the data rows
        ...filteredData.map((row) => [
            row?.lr?.requestid,
            row?.lr?.currcode,
            parseFloat(row?.lr?.requestamount).toFixed(2),
            row?.lr?.requestdate,
            ["No Term", "Daily", "Weekly", "Bi-Weekly", "Monthly", "Yearly"][row?.lr?.termtype],
            row?.lr?.issued === "1" ? "Issued" : "Pending",
            row?.lr?.comments,
            row?.lcr?.cancellationStatus == "1" ? "Yes" : "No"
        ]),
    ];

    const runPDF = () => {
        const doc = new jsPDF();
        const title = "Loan Status Report";
    
        // Title
        doc.setFontSize(18);
        doc.text(title, 14, 22);
    
        // Optional: Filter Info
        const filters = `Status: ${statusFilter || "All"} | Term Type: ${termFilter || "All"}`;
        doc.setFontSize(11);
        doc.text(filters, 14, 30);
    
        // Table Header
        const headers = [
            [
                "Request ID",
                "Product ID",
                "Request Amount",
                "Request Date",
                "Term Type",
                "Status",
                "Comments",
                "Is Cancelled"
            ]
        ];
    
        // Table Body
        const data = filteredData.map((row) => [
            row?.lr?.requestid,
            row?.lr?.currcode,
            parseFloat(row?.lr?.requestamount).toFixed(2),
            row?.lr?.requestdate,
            ["No Term", "Daily", "Weekly", "Bi-Weekly", "Monthly", "Yearly"][row?.lr?.termtype],
            row?.lr?.issued === "1" ? "Issued" : "Pending",
            row?.lr?.comments || "-",
            row?.lcr?.cancellationStatus === "1" ? "Yes" : "No"
        ]);
    
        // Generate PDF Table
        autoTable(doc, {
            head: headers,
            body: data,
            startY: 38,
            styles: {
                fontSize: 8,
                cellPadding: 2,
            },
            headStyles: {
                fillColor: [22, 160, 133],
            }
        });
    
        // Save PDF
        doc.save("LoanStatusReport.pdf");
    };

    useEffect(()=> {
        console.log("WeekStart----------",now.startOf("week").format("YYYY-MM-DD HH:mm:ss"))
        console.log("WeekEnd----------",now.endOf("week").format("YYYY-MM-DD HH:mm:ss"))
        getUserLoanRequests();
    },[]);

    const getUserLoanRequests = async() => {
        try {
            const response = await commonRunQuery(244, selectedUser);
            if(response?.data?.header?.callStatus === "1" && response?.data?.header?.reccount !== "0") {
                const reportsDataArray = Array.isArray(response?.data?.body?.record) ? response?.data?.body?.record : [response?.data?.body?.record];
                console.log("getUserLoanRequests================",reportsDataArray);
                setReportsData(reportsDataArray);
            } else {
                setReportsData([]);
            }
        } catch (error) {
            console.log("getUserLoanRequests_error================",error);
        }
    }

    const termTypeCount = reportsData.reduce((acc, item) => {
        const term = item?.lr.termtype;
        acc[term] = (acc[term] || 0) + 1;
        return acc;
    }, {});

    const termTypeData = Object.keys(termTypeCount).map(term => ({
        name: ["No Term", "Daily", "Weekly", "Bi-Weekly", "Monthly", "Yearly"][term],
        value: termTypeCount[term]
    }));

        // const termTypeData = Object.keys(termTypeCount).map(term => ({
        //     name: [`No Term - (${termTypeCount[term]})`, `Daily - (${termTypeCount[term]})`, `Weekly - (${termTypeCount[term]})`, `Bi-Weekly - (${termTypeCount[term]})`, `Monthly - (${termTypeCount[term]})`, `Yearly - (${termTypeCount[term]})`][term],
        //     value: termTypeCount[term]
        // }));

    const COLORS = ["#0088FE", "#00C49F", "#FF003F", "#FF8042", "#A020F0", "#DC143C"];

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // const filteredData = reportsData.filter((row) => {
    //     const statusMatch = statusFilter ? row?.lr?.issued === (statusFilter === "Issued" ? "1" : "0") : true;
    //     const termMatch = termFilter ? termFilter === termOptions[row?.lr?.termtype] : true;
    //     return statusMatch && termMatch;
    // });

    const issuedCount = reportsData.filter(item => item?.lr?.issued === "1").length;
    const pendingCount = reportsData.filter(item => item?.lr?.issued === "-2").length;

    const issuedData = [
        { name: "Issued", value: issuedCount },
        { name: "Pending", value: pendingCount }
    ];

    return (
        <Box className="user-reports-tab-container">
            <Typography variant="h5" sx={{ mb: 2 }}>Loan Reports Dashboard</Typography>

            {/* {reportsData && reportsData?.length > 0 &&  */}
            <Grid container spacing={3} className="grid-container m-0" sx={{ height: "100%" }}>
                {/* Loans Issued vs Pending - Pie Chart */}
                <Grid item xs={12} sm={6} md={4} className="grid-item">
                    <Card className="grid-cards piechart-card-1"
                        ref={card1Ref}
                        sx={{ 
                            flexGrow: 1, 
                            display: "flex", 
                            flexDirection: "column",
                            height: `${maxHeight}px !important`, 
                            // minHeight: "310px" // Set a fixed minimum height
                        }}>
                        <CardContent sx={{ 
                            flexGrow: 1, 
                            display: "flex", 
                            flexDirection: "column", 
                            justifyContent: "center",
                            transition: "transform 0.3s ease-in-out, filter 0.3s ease-in-out",
                            "&:hover": {
                                transform: "scale(1.02)", 
                                filter: "drop-shadow(5px 5px 10px rgba(0,0,0,0.2))", // Adds a shadow
                            },
                        }}>
                            <Typography variant="h6" align="center">Loans Issued vs Pending</Typography>
                            <Box display="flex" flexDirection="column" alignItems="center">
                                <PieChart width={220} height={220}>
                                    <Pie data={issuedData} cx="50%" cy="40%" outerRadius={60} dataKey="value">
                                        {issuedData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index]} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                    <Legend verticalAlign="bottom" align="center" iconType="circle" />
                                </PieChart>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Loan Term Types - Pie Chart */}
                {reportsData && reportsData?.length > 0 &&
                <Grid item xs={12} sm={6} md={4} className="grid-item">
                    <Card className="grid-cards piechart-card-2"
                        ref={card2Ref}
                        sx={{ 
                            flexGrow: 1, 
                            display: "flex", 
                            flexDirection: "column", 
                            height: `${maxHeight}px`,
                            // minHeight: "310px" // Set the same minHeight as the other card
                        }}>
                        <CardContent sx={{ 
                            flexGrow: 1, 
                            display: "flex", 
                            flexDirection: "column", 
                            justifyContent: "start",
                            transition: "transform 0.3s ease-in-out, filter 0.3s ease-in-out",
                            "&:hover": {
                                transform: "scale(1.02)", 
                                filter: "drop-shadow(5px 5px 10px rgba(0,0,0,0.2))", // Adds a shadow
                            },
                        }}>
                            <Typography variant="h6" align="center" className="mb-3">Loans by Term Type</Typography>
                            <Box display="flex" flexDirection="column" alignItems="center">
                                <PieChart width={220} height={220}>
                                    <Pie data={termTypeData} cx="50%" cy="40%" outerRadius={60} dataKey="value">
                                        {termTypeData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index]} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                    <Legend verticalAlign="bottom" align="center" iconType="circle" />
                                </PieChart>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                }
            </Grid>
            {/* } */}

            {/* Loan Requests Table */}
            <Box sx={{ mt: 4 }} width={"100%"}>
                <Box className="d-flex justify-content-end">
                    {/* <Typography sx={{ display:"flex", alignItems:"flex-end" }} variant="h6">Loan Requests</Typography> */}
                    
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Box>
                                <CSVLink
                                    data={csvData}
                                    filename={`LoanStatusReport.csv`}
                                >
                                    <IconButton
                                        sx={{
                                            width: 40,
                                            height: 40,
                                            color: "#6c757d",
                                            "&:hover": {
                                            backgroundColor: "rgba(108, 117, 125, 0.1)",
                                            },
                                        }}
                                    >
                                        <img src={CSVImage} style={{ width:"48px" }} loading="lazy" />
                                    </IconButton>
                                </CSVLink>{" "}
                            </Box>

                            <Box>
                                <IconButton
                                    onClick={runPDF}
                                    sx={{
                                        width: 40,
                                        height: 40,
                                        color: "#6c757d",
                                        backgroundColor: "transparent",
                                        "&:hover": {
                                            backgroundColor: "rgba(108, 117, 125, 0.1)",
                                        },
                                    }}
                                >
                                    <img src={pdfImage} alt="PDF" loading="lazy" />
                                </IconButton>
                            </Box>
                        </Box>

                        {/* Status Filter */}
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={statusFilter}
                                label="Status"
                                onChange={(e) => {
                                    setStatusFilter(e.target.value);
                                    setPage(0);
                                }}
                            >
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="Issued">Issued</MenuItem>
                                <MenuItem value="Pending">Pending</MenuItem>
                                <MenuItem value="Cancelled">Cancelled</MenuItem>
                            </Select>
                        </FormControl>

                        {/* Term Filter */}
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                            <InputLabel>Term Type</InputLabel>
                            <Select
                                value={termFilter}
                                label="Term Type"
                                onChange={(e) => {
                                    setTermFilter(e.target.value);
                                    setPage(0);
                                }}
                            >
                            <MenuItem value="">All</MenuItem>
                            {termOptions.map((term, index) => (
                                <MenuItem key={index} value={term}>{term}</MenuItem>
                            ))}
                            </Select>
                        </FormControl>
                    </Box>
                </Box>
                <TableContainer className="bordered-table mt-2">
                    <Table>
                        <TableHead>
                            <TableRow>
                                <StyledTableCell>Request ID</StyledTableCell>
                                <StyledTableCell>Product ID</StyledTableCell>
                                <StyledTableCell>Request Amount</StyledTableCell>
                                <StyledTableCell>Request Date</StyledTableCell>
                                <StyledTableCell>Term Type</StyledTableCell>
                                <StyledTableCell>Status</StyledTableCell>
                                <StyledTableCell>Comments</StyledTableCell>
                                <StyledTableCell>Is Cancelled</StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredData && filteredData.length > 0 ? filteredData
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((row, index) => (
                                    <TableRow key={index}>
                                        <TableCell align="center">{row?.lr?.requestid}</TableCell>
                                        <TableCell align="center">{row?.lr?.currcode}</TableCell>
                                        <TableCell align="center">{parseFloat(row?.lr?.requestamount).toLocaleString("en-US", { minimumFractionDigits: 2 })}</TableCell>
                                        <TableCell align="center">{row?.lr?.requestdate}</TableCell>
                                        <TableCell align="center">{["No Term", "Daily", "Weekly", "Bi-Weekly", "Monthly", "Yearly"][row?.lr?.termtype]}</TableCell>
                                        <TableCell align="center">{row?.lr?.issued === "1" ? "Issued" : "Pending"}</TableCell>
                                        <TableCell align="center">{row?.lr?.comments}</TableCell>
                                        <TableCell align="center">{row?.lcr?.cancellationStatus == "1" ? "Yes" : "No"}</TableCell>
                                    </TableRow>
                                ))
                                :
                                <TableRow>
                                    <TableCell align="center" colSpan={8}>
                                        <Typography variant="body1" color="textSecondary">
                                            No data available
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            }
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={filteredData.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Box>
        </Box>
    )
}

export default LoanUserReportTab;